import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { MainHeader } from '@/components/Layouts/Header.tsx';
import { MobileMenu } from '@/components/Layouts/MobileMenu.tsx';
import { useCurrentUser, useNavItems } from '@/components/Layouts/navigation.tsx';
import { PrivateLayer } from '@/components/PrivateLayer.tsx';
import { useIsMobile } from '@/context/IsMobileContext.tsx';
import '@/components/Layouts/app-shell.css';

const homeFor = (role?: string) =>
  role === 'CLIENT' ? '/client' : role === 'ADVISOR' ? '/advisor/clients' : '/';

/** Role a path prefix belongs to; anything outside /client and /advisor is admin. */
const roleForPath = (pathname: string) => {
  if (pathname === '/client' || pathname.startsWith('/client/')) return 'CLIENT';
  if (pathname === '/advisor' || pathname.startsWith('/advisor/')) return 'ADVISOR';
  return 'ADMIN';
};

export const MainLayout = () => {
  const { isMobile } = useIsMobile();
  const { pathname } = useLocation();
  const { data: user } = useCurrentUser();
  const items = useNavItems();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close the sheet whenever the route changes or we grow past the mobile breakpoint.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname, isMobile]);

  // Routes carry a role, but nothing enforced it: a client landing on an admin
  // URL rendered the admin page and every request came back 403.
  if (user && roleForPath(pathname) !== user.role) {
    return <Navigate to={homeFor(user.role)} replace />;
  }

  return (
    <PrivateLayer>
      <div className='app-shell'>
        <MainHeader items={items} user={user} onOpenMobileMenu={() => setMobileMenuOpen(true)} />

        {isMobile && (
          <MobileMenu
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            items={items}
            user={user}
          />
        )}

        <main className='app-shell__content'>
          <div className='app-shell__page'>
            <Outlet />
          </div>
        </main>
      </div>
    </PrivateLayer>
  );
};
